import React, { useState, useEffect, useRef } from "react";
import { Tooltip } from "flowbite-react";
import GenerateFashionModelPromptBuilder from "./GenerateFashionModelPromptBuilder";
import { videoCredits } from "../Utils/VideoGenerationUtils";
import { ChevronDownIcon, ChevronUpIcon, CircleArrowUpIcon, CoinsIcon, DicesIcon, ImagesIcon, MenuIcon, PersonStandingIcon, SettingsIcon, VideoIcon } from "lucide-react";

const PromptContainer = ({
  isProcessing,
  handleGenerateEditImage,
  handleGenerateVideo,
  generationSettings,
  onSettingsChange,
  selectedPrompt,
  onSelectPrompt,
  setShowPromptGalleryModal,
  showPromptGalleryModal,
  disabled,
  selectedMode,
  onModeChange,
}) => {
  // State Management:
  // - `settings` is the current/working settings displayed in UI (currentGenerateSettings)
  // - `defaultGenerateSettings` is the immutable source of defaults (never modified)
  // - Reset functionality uses `defaultGenerateSettings` to restore defaults
  const [settings, setSettings] = useState(generationSettings);
  const [isHidden, setIsHidden] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [
    showGenerateFashionModelPromptBuilderModal,
    setShowGenerateFashionModelPromptBuilderModal,
  ] = useState(false);
  const [showVideoSettings, setShowVideoSettings] = useState(false);
  const [hasUnappliedChanges, setHasUnappliedChanges] = useState(false);
  const [isApplyingSettings, setIsApplyingSettings] = useState(false);

  // Refs for settings modal and button to detect outside clicks
  const settingsModalRef = useRef(null);
  const settingsButtonRef = useRef(null);
  const showGenerateFashionModelPromptBuilderModalRef = useRef(null);
  const promptGalleryModalRef = useRef(null);

  // Close settings modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the modal AND the settings button
      if (
        settingsModalRef.current &&
        !settingsModalRef.current.contains(event.target) &&
        settingsButtonRef.current &&
        !settingsButtonRef.current.contains(event.target)
      ) {
        setShowSettings(false);
        setShowVideoSettings(false);
      }

      if (
        showGenerateFashionModelPromptBuilderModalRef.current &&
        !showGenerateFashionModelPromptBuilderModalRef.current.contains(
          event.target
        ) &&
        promptGalleryModalRef.current &&
        !promptGalleryModalRef.current.contains(event.target)
      ) {
        setShowGenerateFashionModelPromptBuilderModal(false);
      }
    };

    if (
      showSettings ||
      showVideoSettings ||
      showGenerateFashionModelPromptBuilderModal
    ) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    showSettings,
    showVideoSettings,
    showGenerateFashionModelPromptBuilderModal,
  ]);

  // Sync selectedPrompt to settings.prompt when it changes
  useEffect(() => {
    if (selectedPrompt) {
      setSettings((prevSettings) => {
        const newSettings = {
          ...prevSettings,
          prompt: selectedPrompt,
        };
        // Immediately propagate to parent so it's applied
        onSettingsChange?.(newSettings);
        return newSettings;
      });
    }
  }, [selectedPrompt, onSettingsChange]);

  // Apply settings 
  const applySettings = () => {
    setIsApplyingSettings(true);
    onSettingsChange?.(settings);
    setHasUnappliedChanges(false);

    setTimeout(() => {
      setIsApplyingSettings(false);
      setShowSettings(false);
      setShowVideoSettings(false);
    }, 2000);
  };

  // Reset settings to default for current model
  const resetToDefaults = () => {
    const defaultSettings = getDefaultSettings(selectedMode, settings.model);
    const newSettings = {
      ...defaultSettings,
      prompt: settings.prompt, // Preserve prompt on reset
    };
    setSettings(newSettings);
    onSettingsChange?.(newSettings); // Apply immediately on reset
    setHasUnappliedChanges(false);
  };

  const handleSettingChange = (key, value) => {
    let newSettings = { ...settings, [key]: value };

    // When model changes, intelligently merge settings based on what the new model supports
    // Uses defaultGenerateSettings (via getDefaultSettings) for fallback values
    if (key === "model") {
      const newModelSettings = getModelSettings(selectedMode, value);
      const modelDefaults = getDefaultSettings(selectedMode, value); // Gets from defaultGenerateSettings

      // Build new settings object with only supported settings
      const mergedSettings = { model: value, prompt: settings.prompt };

      // For each setting the new model supports, keep existing value if valid, otherwise use default
      Object.keys(newModelSettings || {}).forEach((settingKey) => {
        const availableOptions = newModelSettings[settingKey];
        const currentValue = settings[settingKey];

        if (Array.isArray(availableOptions)) {
          // If it's an array of options, check if current value is valid
          if (availableOptions.includes(currentValue)) {
            mergedSettings[settingKey] = currentValue;
          } else {
            mergedSettings[settingKey] = modelDefaults[settingKey];
          }
        } else if (
          availableOptions?.type === "number" ||
          availableOptions?.type === "range"
        ) {
          // For number/range settings, keep current value if within bounds, otherwise use default
          const min = availableOptions.min;
          const max = availableOptions.max;
          if (
            currentValue !== undefined &&
            currentValue >= min &&
            currentValue <= max
          ) {
            mergedSettings[settingKey] = currentValue;
          } else {
            mergedSettings[settingKey] = modelDefaults[settingKey];
          }
        } else {
          // For other setting types, use default
          mergedSettings[settingKey] = modelDefaults[settingKey];
        }
      });

      newSettings = mergedSettings;
    }

    setSettings(newSettings);

    // Mark that there are unapplied changes (except for prompt which applies immediately)
    if (key !== "prompt") {
      setHasUnappliedChanges(true);
    } else {
      // Prompt changes apply immediately for better typing experience
      onSettingsChange?.(newSettings);
    }
  };

  const handleModeSelection = (mode) => {
    // When mode changes, load defaults for the default model of that mode
    // Uses defaultGenerateSettings (via getDefaultSettings) to initialize
    const defaultModel = mode === "image" ? "flux-kontext-pro" : "sora";
    const modeDefaults = getDefaultSettings(mode, defaultModel); // Gets from defaultGenerateSettings

    // Preserve the prompt when switching modes
    const newSettings = {
      ...modeDefaults,
      prompt: settings.prompt,
    };

    setSettings(newSettings);
    onSettingsChange?.(newSettings); // Apply immediately when switching modes
    setHasUnappliedChanges(false);
    onModeChange(mode);
  };

  const handleGenerate = () => {
    console.log("handleGenerate", selectedMode);
    if (selectedMode === "image") {
      handleGenerateEditImage();
    } else if (selectedMode === "video") {
      handleGenerateVideo();
    }
  };

  // Model metadata for display purposes
  const modelMetadata = {
    "flux-kontext-pro": {
      displayName: "FLUX Kontext Pro",
      description: "Balanced performance",
      credits: 27,
    },
    "flux-kontext-max": {
      displayName: "FLUX Kontext Max",
      description: "Maximum quality",
      credits: 27,
    },
    "flux-2-pro": {
      displayName: "FLUX 2 Pro",
      description: "Fast and balanced",
      credits: 27,
    },
    "flux-2-flex": {
      displayName: "FLUX 2 Flex",
      description: "Fast and flexible",
      credits: 27,
    },
    sora: {
      displayName: "Video",
      description: "An 8 second clip at 1080p.",
    },
    // "ray-1.6": {
    //   displayName: "Ray 1.6",
    //   description: "Fast generation",
    // },
    // "ray-2.0": {
    //   displayName: "Ray 2.0",
    //   description: "Enhanced quality",
    // },
    // "ray-flash-2": {
    //   displayName: "Ray Flash 2",
    //   description: "Ultra-fast",
    // },
    // Future models can be added here
    // "dall-e-3": {
    //   displayName: "DALL-E 3",
    //   description: "OpenAI's image model",
    //   credits: 8,
    // },
  };

  const aspectRatios = [
    { value: "default", label: "Default", width: 1, height: 1 },
    { value: "16:9", label: "16:9", width: 16, height: 9 },
    { value: "9:16", label: "9:16", width: 9, height: 16 },
    { value: "1:1", label: "1:1", width: 1, height: 1 },
    { value: "4:3", label: "4:3", width: 4, height: 3 },
    { value: "3:4", label: "3:4", width: 3, height: 4 },
  ];

  const settingsOptions = {
    image: {
      // List of all available models
      models: [
        "flux-kontext-pro",
        "flux-kontext-max",
        "flux-2-pro",
        "flux-2-flex",
      ],

      // Model-specific settings (allows different providers to have different capabilities)
      "flux-kontext-pro": {
        aspect_ratio: ["default", "1:1", "16:9", "9:16", "4:3", "3:4"],
        prompt_upsampling: [true, false],
        output_format: ["jpeg", "png"],
        seed: { type: "number", min: 0, max: 1000000 },
        safety_tolerance: { type: "range", min: 2, max: 6, step: 0.1 },
      },
      "flux-kontext-max": {
        aspect_ratio: ["default", "1:1", "16:9", "9:16", "4:3", "3:4"],
        prompt_upsampling: [true, false],
        output_format: ["jpeg", "png"],
        seed: { type: "number", min: 0, max: 1000000 },
        safety_tolerance: { type: "range", min: 2, max: 6, step: 0.1 },
      },
      "flux-2-pro": {
        width: { type: "number" },
        height: { type: "number" },
        seed: { type: "number", min: 0, max: 1000000 },
        safety_tolerance: { type: "range", min: 2, max: 6, step: 0.1 },
        output_format: ["jpeg", "png"],
      },
      "flux-2-flex": {
        width: { type: "number" },
        height: { type: "number" },
        guidance: { type: "number", min: 0, max: 15 },
        steps: { type: "number", min: 1, max: 50 },
        seed: { type: "number", min: 0, max: 1000000 },
        safety_tolerance: { type: "range", min: 2, max: 6, step: 0.1 },
        output_format: ["jpeg", "png"],
      },

      // Example of how to add models from other providers in the future:
      // "dall-e-3": {
      //   "aspect_ratio": ["1:1", "16:9", "9:16"], // Different options
      //   "quality": ["standard", "hd"],
      //   "style": ["natural", "vivid"],
      //   // No prompt_upsampling or safety_tolerance for DALL-E
      // },
      // "midjourney-v6": {
      //   "aspect_ratio": ["1:1", "2:3", "3:2", "16:9", "9:16"],
      //   "stylize": { type: "range", min: 0, max: 1000, step: 1 },
      //   "chaos": { type: "range", min: 0, max: 100, step: 1 },
      //   "quality": [0.25, 0.5, 1, 2],
      //   // Different settings entirely
      // },
    },
    video: {
      // List of all available models
      models: [
        "sora",
        // "ray-1.6", "ray-2.0", "ray-flash-2"
      ],

      // Model-specific settings
      sora: {
        aspect_ratio: ["16:9", "9:16"],
        resolution: ["720p", "1080p", "4k"],
        duration: ["4", "6", "8"],
      },
      // "ray-1.6": {
      //   "aspect_ratio": ["1:1", "16:9", "9:16", "3:4", "4:3"],
      //   "resolution": ["480p", "720p", "1080p", "4k", "Custom"],
      //   "duration": ["5s", "9s", "Custom"],
      // },
      // "ray-2.0": {
      //   "aspect_ratio": ["1:1", "16:9", "9:16", "3:4", "4:3"],
      //   "resolution": ["480p", "720p", "1080p", "4k", "Custom"],
      //   "duration": ["5s", "9s", "Custom"],
      // },
      // "ray-flash-2": {
      //   "aspect_ratio": ["1:1", "16:9", "9:16", "3:4", "4:3"],
      //   "resolution": ["480p", "720p", "1080p", "4k", "Custom"],
      //   "duration": ["5s", "9s", "Custom"],
      // },
    },
  };

  const defaultGenerateSettings = {
    image: {
      // Model-specific defaults for image
      "flux-kontext-pro": {
        model: "flux-kontext-pro",
        aspect_ratio: "1:1",
        prompt_upsampling: false,
        output_format: "jpeg",
        seed: 42,
        safety_tolerance: 4,
        prompt: "",
      },
      "flux-kontext-max": {
        model: "flux-kontext-max",
        aspect_ratio: "1:1",
        prompt_upsampling: false,
        output_format: "jpeg",
        seed: 42,
        safety_tolerance: 4,
        prompt: "",
      },
      "flux-2-pro": {
        model: "flux-2-pro",
        width: 512,
        height: 512,
        seed: 42,
        safety_tolerance: 4,
        output_format: "jpeg",
        prompt: "",
      },
      "flux-2-flex": {
        model: "flux-2-flex",
        width: 512,
        height: 512,
        guidance: 7.5,
        steps: 25,
        seed: 42,
        safety_tolerance: 4,
        output_format: "jpeg",
        prompt: "",
      },

      // Example defaults for future models:
      // "dall-e-3": {
      //   "model": "dall-e-3",
      //   "aspect_ratio": "1:1",
      //   "quality": "standard",
      //   "style": "natural",
      //   "prompt": "",
      // },
      // "stable-diffusion-xl": {
      //   "model": "stable-diffusion-xl",
      //   "aspect_ratio": "1:1",
      //   "output_format": "png",
      //   "seed": 42,
      //   "guidance_scale": 7.5,
      //   "num_inference_steps": 50,
      //   "prompt": "",
      // },
    },
    video: {
      // Model-specific defaults for video
      sora: {
        model: "sora",
        aspect_ratio: "16:9",
        resolution: "1080p",
        duration: "8",
        prompt: "",
      },
      // "ray-1.6": {
      //   "model": "ray-1.6",
      //   "aspect_ratio": "1:1",
      //   "resolution": "1080p",
      //   "duration": "5",
      //   "prompt": "",
      // },
      // "ray-2.0": {
      //   "model": "ray-2.0",
      //   "aspect_ratio": "1:1",
      //   "resolution": "1080p",
      //   "duration": "5",
      //   "prompt": "",
      // },
      // "ray-flash-2": {
      //   "model": "ray-flash-2",
      //   "aspect_ratio": "1:1",
      //   "resolution": "1080p",
      //   "duration": "5",
      //   "prompt": "",
      // },
    },
  };

  // Helper function to get available settings for a specific model
  const getModelSettings = (mode, model) => {
    if (mode === "image") {
      return (
        settingsOptions.image[model] ||
        settingsOptions.image["flux-kontext-pro"]
      );
    } else if (mode === "video") {
      return settingsOptions.video[model] || settingsOptions.video["sora"];
    }
    return null;
  };

  // Helper function to get default settings for a given mode and model
  const getDefaultSettings = (mode, model = null) => {
    if (mode === "image") {
      const defaultModel = model || "flux-kontext-pro";
      return (
        defaultGenerateSettings.image[defaultModel] ||
        defaultGenerateSettings.image["flux-kontext-pro"]
      );
    } else if (mode === "video") {
      const defaultModel = model || "sora";
      return (
        defaultGenerateSettings.video[defaultModel] ||
        defaultGenerateSettings.video["sora"]
      );
    }
    return defaultGenerateSettings.image["flux-kontext-pro"]; // fallback
  };

  // Helper function to get list of available models for a mode
  const getAvailableModels = (mode) => {
    return settingsOptions[mode]?.models || [];
  };

  const generateRandomSeed = () => {
    handleSettingChange("seed", Math.floor(Math.random() * 1000000));
  };

  // Removed local calculateVideogenerationCost - now using utility from VideoGenerationUtils

  const settingsModal = () => {
    if (selectedMode === "image") {
      return (
        <div
          ref={settingsModalRef}
          className="absolute bottom-full left-0 z-[100] mb-2 w-72 rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)]"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="font-Mulish text-sm font-semibold text-secondary">Edit</p>
          <p className="mt-1 font-Mulish text-sm text-graphite">
            An edit sends this photo with your prompt. It uses 27 credits.
          </p>
        </div>
      );
    }
    return (
      <div
        ref={settingsModalRef}
        className="absolute bottom-full left-0 z-[100] mb-2 flex w-[550px] max-h-[calc(100vh-200px)] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="overflow-y-auto px-5 py-4 space-y-3.5 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
          <div className="flex items-center justify-between pb-2 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedMode === "image"
                ? "Image Generation Settings"
                : "Video Generation Settings"}
            </h3>

            <div className="flex items-center gap-3">
              {selectedMode === "image" &&
                modelMetadata[settings.model]?.credits && (
                  <div className="flex items-center gap-1 text-sm font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded-lg">
                    <span>Cost:</span>
                    <span className="font-bold">
                      {modelMetadata[settings.model].credits} credits
                    </span>
                  </div>
                )}

              {selectedMode === "video" && (
                <div className="flex items-center gap-1 text-sm font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded-lg">
                  <span>Cost:</span>
                  <span className="font-bold">{videoCredits(settings.duration, settings.resolution)} credits</span>
                </div>
              )}

              <button
                onClick={() => {
                  setShowSettings(false);
                  setShowVideoSettings(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                title="Close settings"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Select Model - Dynamic based on available models */}
          <div className="space-y-2 pb-3.5 border-b border-gray-200">
            <label className="text-sm font-semibold text-gray-700">
              Select Model
            </label>
            <div className="grid grid-cols-2 gap-2">
              {getAvailableModels(selectedMode).map((model) => {
                const metadata = modelMetadata[model] || {
                  displayName: model,
                  description: "",
                };
                const isSelected = settings.model === model;

                return (
                  <button
                    key={model}
                    onClick={() => handleSettingChange("model", model)}
                    className={`group py-2 px-3 border rounded-xl text-sm font-medium transition-all duration-200 ${
                      isSelected
                        ? "bg-white text-primary-600 border-primary-600 shadow-sm"
                        : "bg-white border-gray-300 text-gray-900 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    {metadata.description ? (
                      <div className="text-left">
                        <div className="font-semibold">
                          {metadata.displayName}
                        </div>
                        <div
                          className={`text-xs ${
                            isSelected
                              ? "text-primary-500"
                              : "text-gray-500 group-hover:text-primary-500"
                          }`}
                        >
                          {metadata.description}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">{metadata.displayName}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Aspect Ratio - Dynamic based on model settings */}
          {(() => {
            const modelSettings = getModelSettings(
              selectedMode,
              settings.model
            );
            const availableRatios = modelSettings?.aspect_ratio || [];

            if (availableRatios.length === 0) return null;

            return (
              <div className="space-y-2 pb-3.5 border-b border-gray-200">
                <label className="text-sm font-semibold text-gray-700">
                  Aspect Ratio
                </label>
                <div className="flex flex-wrap gap-2">
                  {availableRatios.map((ratioValue) => {
                    const ratioData = aspectRatios.find(
                      (r) => r.value === ratioValue
                    );
                    const isSelected = settings.aspect_ratio === ratioValue;

                    // Show visual aspect ratio boxes for all modes
                    if (ratioData) {
                      return (
                        <button
                          key={ratioValue}
                          onClick={() =>
                            handleSettingChange("aspect_ratio", ratioValue)
                          }
                          className={`relative group w-[72px] h-[72px] border rounded-lg transition-all duration-200 flex items-center justify-center p-2 ${
                            isSelected
                              ? "bg-white text-primary-600 border-primary-600 shadow-sm"
                              : "bg-white border-gray-300 text-gray-900 hover:border-primary-300 hover:shadow-sm"
                          }`}
                        >
                          <div
                            className={`rounded-md transition-colors duration-200 ${
                              isSelected
                                ? "bg-primary-100"
                                : "bg-gray-100 group-hover:bg-primary-50"
                            }`}
                            style={{
                              width:
                                ratioData.width > ratioData.height
                                  ? "56px"
                                  : `${
                                      (ratioData.width / ratioData.height) * 56
                                    }px`,
                              height:
                                ratioData.height > ratioData.width
                                  ? "56px"
                                  : `${
                                      (ratioData.height / ratioData.width) * 56
                                    }px`,
                            }}
                          />
                          <span
                            className={`absolute inset-0 flex items-center justify-center text-[10px] font-semibold ${
                              isSelected
                                ? "text-primary-700"
                                : "text-gray-600 group-hover:text-primary-600"
                            }`}
                          >
                            {ratioData.label}
                          </span>
                        </button>
                      );
                    }

                    // Fallback for unknown ratios (show simple button)
                    return (
                      <button
                        key={ratioValue}
                        onClick={() =>
                          handleSettingChange("aspect_ratio", ratioValue)
                        }
                        className={`py-2 px-3 border rounded-lg text-xs font-semibold transition-all duration-200 ${
                          isSelected
                            ? "bg-white text-primary-600 border-primary-600 shadow-sm"
                            : "bg-white border-gray-300 text-gray-700 hover:border-primary-300 hover:shadow-sm"
                        }`}
                      >
                        {ratioValue}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Size (Width × Height)  flux-2-pro flux-2-flex  */}
          {(() => {
            const modelSettings = getModelSettings(
              selectedMode,
              settings.model
            );

            if (!modelSettings?.width && !modelSettings?.height) return null;

            return (
              <div className="space-y-2 pb-3.5 border-b border-gray-200">
                <label className="text-sm font-semibold text-gray-700">
                  Size (Width × Height)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {modelSettings?.width && (
                    <div className="space-y-1.5">
                      <span className="text-xs font-medium text-gray-600 flex justify-start">
                        Width (px)
                      </span>
                      <input
                        type="number"
                        value={settings.width ?? modelSettings.width.min}
                        min={modelSettings.width.min}
                        max={modelSettings.width.max}
                        onChange={(e) =>
                          handleSettingChange(
                            "width",
                            Number(e.target.value) || modelSettings.width.min
                          )
                        }
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-xs font-medium text-gray-900 focus:ring-2 focus:ring-primary-100 focus:border-primary-500 outline-none transition-all"
                      />
                    </div>
                  )}

                  {modelSettings?.height && (
                    <div className="space-y-1.5">
                      <span className="text-xs font-medium text-gray-600 flex justify-start">
                        Height (px)
                      </span>
                      <input
                        type="number"
                        value={settings.height ?? modelSettings.height.min}
                        min={modelSettings.height.min}
                        max={modelSettings.height.max}
                        onChange={(e) =>
                          handleSettingChange(
                            "height",
                            Number(e.target.value) || modelSettings.height.min
                          )
                        }
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-xs font-medium text-gray-900 focus:ring-2 focus:ring-primary-100 focus:border-primary-500 outline-none transition-all"
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Resolution - Dynamic based on model settings */}
          {(() => {
            const modelSettings = getModelSettings(
              selectedMode,
              settings.model
            );
            const availableResolutions = modelSettings?.resolution;

            if (!availableResolutions) return null;

            const gridCols =
              availableResolutions.length <= 3 ? "grid-cols-3" : "grid-cols-5";

            return (
              <div className="space-y-2 pb-3.5 border-b border-gray-200">
                <label className="text-sm font-semibold text-gray-700">
                  Resolution
                </label>
                <div className={`grid gap-2 ${gridCols}`}>
                  {availableResolutions.map((res) => (
                    <button
                      key={res}
                      onClick={() => handleSettingChange("resolution", res)}
                      className={`py-2 px-3 border rounded-lg text-xs font-semibold transition-all duration-200 ${
                        settings.resolution === res
                          ? "bg-white text-primary-600 border-primary-600 shadow-sm"
                          : "bg-white border-gray-300 text-gray-700 hover:border-primary-300 hover:shadow-sm"
                      }`}
                    >
                      {res}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Duration - Dynamic based on model settings */}
          {(() => {
            const modelSettings = getModelSettings(
              selectedMode,
              settings.model
            );
            const availableDurations = modelSettings?.duration;

            if (!availableDurations) return null;

            const gridCols =
              availableDurations.length <= 3 ? "grid-cols-3" : "grid-cols-4";

            return (
              <div className="space-y-2 pb-3.5 border-b border-gray-200">
                <label className="text-sm font-semibold text-gray-700">
                  Duration
                </label>
                <div className={`grid gap-2 ${gridCols}`}>
                  {availableDurations.map((dur) => (
                    <button
                      key={dur}
                      onClick={() => handleSettingChange("duration", dur)}
                      className={`py-2 px-3 border rounded-lg text-xs font-semibold transition-all duration-200 ${
                        settings.duration === dur
                          ? "bg-white text-primary-600 border-primary-600 shadow-sm"
                          : "bg-white border-gray-300 text-gray-700 hover:border-primary-300 hover:shadow-sm"
                      }`}
                    >
                      {dur}s
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Image-specific settings - Dynamic based on model capabilities */}
          {(() => {
            const modelSettings = getModelSettings(
              selectedMode,
              settings.model
            );

            return (
              <>
                {/* Prompt Upsampling */}
                {modelSettings?.prompt_upsampling && (
                  <div className="pb-3.5 border-b border-gray-200">
                    <div
                      onClick={() =>
                        handleSettingChange(
                          "prompt_upsampling",
                          !settings.prompt_upsampling
                        )
                      }
                      className={`flex items-center justify-between py-2.5 px-3.5 rounded-lg border cursor-pointer transition-all duration-200 ${
                        settings.prompt_upsampling
                          ? "bg-primary-50 border-primary-600 shadow-sm"
                          : "bg-gray-50 border-gray-300 hover:border-primary-300 hover:bg-primary-50/50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="prompt_upsampling"
                          checked={settings.prompt_upsampling}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSettingChange(
                              "prompt_upsampling",
                              e.target.checked
                            );
                          }}
                          className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-2 focus:ring-primary-500/20 cursor-pointer"
                        />
                        <label
                          htmlFor="prompt_upsampling"
                          className="text-sm font-semibold text-gray-700 cursor-pointer"
                        >
                          Prompt Upsampling
                        </label>
                      </div>
                      {settings.prompt_upsampling && (
                        <span className="text-[10px] font-medium text-primary-600 bg-white px-2 py-0.5 rounded-md border border-primary-200">
                          Enabled
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Output Format */}
                {modelSettings?.output_format && (
                  <div className="space-y-2 pb-3.5 border-b border-gray-200">
                    <label className="text-sm font-semibold text-gray-700">
                      Output Format
                    </label>
                    <div className="flex items-center gap-2">
                      {modelSettings.output_format.map((format) => (
                        <button
                          key={format}
                          onClick={() =>
                            handleSettingChange("output_format", format)
                          }
                          className={`flex-1 py-2 px-3 text-xs font-semibold rounded-lg border transition-all duration-200 ${
                            settings.output_format === format
                              ? "bg-white text-primary-600 border-primary-600 shadow-sm"
                              : "bg-white border-gray-300 text-gray-700 hover:border-primary-300 hover:shadow-sm"
                          }`}
                        >
                          {format.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Seed and Safety Tolerance - Combined Row */}
                {(modelSettings?.seed || modelSettings?.safety_tolerance) && (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Seed */}
                    {modelSettings?.seed && (
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700 flex justify-between items-center">
                          <span>Seed</span>
                          <span className="text-[10px] font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-md">
                            {settings.seed}
                          </span>
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            value={settings.seed}
                            min={modelSettings.seed.min || 0}
                            max={modelSettings.seed.max || 1000000}
                            onChange={(e) =>
                              handleSettingChange(
                                "seed",
                                parseInt(e.target.value) || 42
                              )
                            }
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 text-xs font-medium focus:ring-2 focus:ring-primary-100 focus:border-primary-500 outline-none transition-all"
                          />
                          <button
                            onClick={generateRandomSeed}
                            className="px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-primary-50 hover:border-primary-300 transition-all duration-200 group"
                            title="Generate random seed"
                          >
                            <DicesIcon className="w-3.5 h-3.5 text-gray-500 group-hover:text-primary-600 transition-colors duration-200" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Safety Tolerance */}
                    {modelSettings?.safety_tolerance && (
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700 flex justify-between items-center">
                          <span>Safety Tolerance</span>
                          <span className="text-[10px] font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-md">
                            {settings.safety_tolerance?.toFixed(1) ||
                              modelSettings.safety_tolerance.min}
                          </span>
                        </label>
                        <div className="px-1">
                          <input
                            type="range"
                            min={modelSettings.safety_tolerance.min}
                            max={modelSettings.safety_tolerance.max}
                            step={modelSettings.safety_tolerance.step}
                            value={
                              settings.safety_tolerance ||
                              modelSettings.safety_tolerance.min
                            }
                            onChange={(e) =>
                              handleSettingChange(
                                "safety_tolerance",
                                parseFloat(e.target.value)
                              )
                            }
                            className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gradient-to-r [&::-webkit-slider-thumb]:from-primary-500 [&::-webkit-slider-thumb]:to-primary-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:hover:scale-110 [&::-webkit-slider-thumb]:transition-transform [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gradient-to-r [&::-moz-range-thumb]:from-primary-500 [&::-moz-range-thumb]:to-primary-600 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:shadow-md"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/*  Guidance & Steps sirf flux-2-flex */}
                {((modelSettings?.guidance &&
                  modelSettings.guidance.type === "number") ||
                  (modelSettings?.steps &&
                    modelSettings.steps.type === "number")) && (
                  <div className="grid grid-cols-2 gap-4 mt-3 pb-3.5 ">
                    {modelSettings?.guidance && (
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700 flex justify-start">
                          Guidance
                        </label>
                        <input
                          type="number"
                          value={
                            settings.guidance ?? modelSettings.guidance.min ?? 0
                          }
                          min={modelSettings.guidance.min}
                          max={modelSettings.guidance.max}
                          step={modelSettings.guidance.step || 0.5}
                          onChange={(e) =>
                            handleSettingChange(
                              "guidance",
                              Number(e.target.value) ||
                                modelSettings.guidance.min
                            )
                          }
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-xs font-medium text-gray-900 focus:ring-2 focus:ring-primary-100 focus:border-primary-500 outline-none transition-all"
                        />
                      </div>
                    )}

                    {modelSettings?.steps && (
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700 flex justify-start">
                          Steps
                        </label>
                        <input
                          type="number"
                          value={settings.steps ?? modelSettings.steps.min ?? 1}
                          min={modelSettings.steps.min}
                          max={modelSettings.steps.max}
                          step={modelSettings.steps.step || 1}
                          onChange={(e) =>
                            handleSettingChange(
                              "steps",
                              Number(e.target.value) || modelSettings.steps.min
                            )
                          }
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-xs font-medium text-gray-900 focus:ring-2 focus:ring-primary-100 focus:border-primary-500 outline-none transition-all"
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}

          {/* Action Buttons */}
          <div className="pt-3 border-t border-gray-200">
            <div className="flex gap-2">
              <button
                onClick={resetToDefaults}
                className="flex-1 py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 rounded-lg text-xs font-semibold transition-all duration-200 hover:shadow-sm border border-gray-200 hover:border-gray-300"
              >
                Reset
              </button>
              <button
                onClick={applySettings}
                disabled={!hasUnappliedChanges || isApplyingSettings}
                className={`flex-1 py-2 px-4 rounded-lg text-xs font-semibold transition-all duration-200 border-2 flex items-center justify-center gap-2 ${
                  isApplyingSettings
                    ? "bg-primary-600 text-white border-primary-600 cursor-wait"
                    : hasUnappliedChanges
                    ? "bg-primary-600 hover:bg-primary-700 text-white border-primary-600 hover:border-primary-700 shadow-md hover:shadow-lg hover:scale-105"
                    : "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                }`}
              >
                {isApplyingSettings && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                {isApplyingSettings ? "Applying..." : hasUnappliedChanges ? "Apply Settings" : "Settings Applied"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="studio-prompt absolute bottom-4 left-1/2 -translate-x-1/2 w-[768px] max-w-[90vw] z-10">
      <div
        className={`transition-all duration-500 ease-in-out ${
          isHidden
            ? "top-full opacity-0 pointer-events-none"
            : "top-0 translate-y-0 opacity-100"
        }`}
      >
        <div className="relative rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)]">
          <div className="relative flex flex-col gap-2 p-2">
            <div className="w-full flex justify-between items-center gap-2">
              <textarea
                placeholder="Please describe the image you want or the specific edits you need in detail. For example: 'A sunset over a calm mountain lake' or 'Change the background to a beach with waves'"
                className="w-full resize-none rounded-lg border-0 bg-white px-3 py-2 font-Mulish text-sm text-secondary focus:ring-0"
                style={{
                  outline: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  height: "auto",
                  overflow: "hidden",
                }}
                value={settings.prompt}
                onChange={(e) => handleSettingChange("prompt", e.target.value)}
                rows={3}
                onInput={(e) => {
                  e.target.style.height = "auto";
                  e.target.style.height =
                    Math.min(e.target.scrollHeight, 120) + "px";
                }}
              />
              <button
                onClick={handleGenerate}
                className="flex-shrink-0 text-white rounded-full text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <CircleArrowUpIcon className="w-7 h-7 text-secondary opacity-80 hover:opacity-90 transition-opacity duration-200" />
                )}
              </button>
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="inline-flex overflow-hidden rounded-lg border border-hairline">
                  <Tooltip
                    content="Image Generation"
                    className="text-xs bg-gray-800 text-white p-1 rounded"
                  >
                    <button
                      onClick={() => handleModeSelection("image")}
                      className={`flex items-center justify-center gap-1 px-2.5 py-1 font-Mulish text-xs ${
                        selectedMode === "image"
                          ? "border-b-2 border-primary-500 font-semibold text-secondary"
                          : "text-graphite"
                      }`}
                    >
                      <ImagesIcon
                        className={`h-4 w-4 ${
                          selectedMode === "image" ? "text-primary-700" : "text-graphite"
                        }`}
                      />
                    </button>
                  </Tooltip>
                  <Tooltip
                    content="Video Generation"
                    className="text-xs bg-gray-800 text-white p-1 rounded"
                  >
                    <button
                      onClick={() => handleModeSelection("video")}
                      className={`flex items-center justify-center gap-1 border-l border-hairline px-2.5 py-1 font-Mulish text-xs ${
                        selectedMode === "video"
                          ? "border-b-2 border-primary-500 font-semibold text-secondary"
                          : "text-graphite"
                      }`}
                    >
                      <VideoIcon
                        className={`h-4 w-4 ${
                          selectedMode === "video" ? "text-primary-700" : "text-graphite"
                        }`}
                      />
                    </button>
                  </Tooltip>
                </div>

                <Tooltip
                  content="Settings"
                  className="text-xs bg-gray-800 text-white p-1 rounded"
                >
                  <button
                    ref={settingsButtonRef}
                    onClick={() => {
                      if (selectedMode === "image") {
                        setShowSettings(!showSettings);
                        setShowVideoSettings(false);
                      } else {
                        setShowVideoSettings(!showVideoSettings);
                        setShowSettings(false);
                      }
                    }}
                    className={`relative flex items-center justify-center rounded-full border px-2 py-1 ${
            showSettings || showVideoSettings
              ? "border-primary-500 bg-primary-50 text-primary-700"
              : "border-primary-200 bg-white text-primary-700 hover:bg-primary-50"
          }`}
                  >
                    <SettingsIcon className="h-4 w-4 text-primary-700" />
                    {(showSettings || showVideoSettings) && settingsModal()}
                  </button>
                </Tooltip>

                <div className="hidden">
                  <button
                    onClick={() => {
                      setShowVideoSettings(!showVideoSettings);
                      setShowSettings(false);
                    }}
                    className={`flex items-center gap-2 px-2.5 py-1 text-xs rounded-xl transition-all duration-200 border
          ${
            showVideoSettings
              ? "bg-primary-600 text-white border-primary-600 shadow-sm"
              : "bg-primary-50 text-primary-600 hover:bg-primary-100 hover:text-primary-700 border-primary-200"
          }`}
                  >
                    <VideoIcon
                      className={`w-4 h-4 ${
                        showVideoSettings ? "text-white" : "text-primary-600"
                      }`}
                    />
                    <span>Video Resize</span>
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  {/* Current Settings Summary */}
                  <div className="flex items-center gap-1.5 rounded-full border border-hairline bg-white px-2.5 py-1 font-Mulish text-xs text-graphite">
                    {selectedMode === "image" ? (
                      <span className="font-semibold text-secondary">Edit</span>
                    ) : (
                      <>
                        <span className="font-semibold text-secondary">Video</span>
                        <span className="text-gray-400">•</span>
                        <span>{settings.aspect_ratio}</span>
                      </>
                    )}
                    {selectedMode === "video" && settings.duration && (
                      <>
                        <span className="text-gray-400">•</span>
                        <span>{settings.duration}s</span>
                      </>
                    )}
                    <span className="text-gray-400">•</span>
                    {selectedMode === "image" && (
                        <div>
                          <span className="font-semibold">27</span>
                          <span className="text-stone-500">&nbsp;credits</span>
                        </div>
                      )}
                    {selectedMode === "video" && (
                      <div>
                        <span className="font-semibold">{videoCredits(settings.duration, settings.resolution)}</span>
                        <span className="text-stone-500">&nbsp;Credits</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary-50 hover:bg-primary-100 text-primary-600 rounded-xl cursor-pointer border border-primary-200 hover:border-primary-300 hover:shadow-sm"
                  onClick={() =>
                    setShowGenerateFashionModelPromptBuilderModal(
                      !showGenerateFashionModelPromptBuilderModal
                    )
                  }
                  ref={promptGalleryModalRef}
                >
                  <PersonStandingIcon className="w-4 h-4" />
                  <span className="tracking-wide">Fashion Model</span>
                </div>

                <div
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary-50 hover:bg-primary-100 text-primary-600 rounded-xl cursor-pointer border border-primary-200 hover:border-primary-300 hover:shadow-sm"
                  onClick={() => showPromptGalleryModal("all")}
                >
                  <MenuIcon className="w-4 h-4" />
                  <span className="tracking-wide">Prompt Gallery</span>
                </div>

                <button
                  onClick={() => setIsHidden(!isHidden)}
                  className="flex items-center justify-center py-1 px-2 text-primary-500 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-xl shadow-sm transition-all"
                >
                  <div className="flex items-center gap-1">
                    <ChevronDownIcon className="w-4 h-4 text-primary-500" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={() => setIsHidden(!isHidden)}
        className={`absolute z-20 right-2 flex items-center text-primary-500 justify-center py-1 px-2 bg-primary-50 hover:bg-primary-100 border border-primary-200 hover:border-primary-300 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 ${
          isHidden
            ? "bottom-0 right-0 opacity-100"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex items-center justify-center gap-1">
          <ChevronUpIcon className="w-4 h-4 text-primary-500" />
        </div>
      </button>

      {showGenerateFashionModelPromptBuilderModal && (
        <GenerateFashionModelPromptBuilder
          setShowGenerateFashionModelPromptBuilderModal={
            setShowGenerateFashionModelPromptBuilderModal
          }
          onSelectPrompt={onSelectPrompt}
          showGenerateFashionModelPromptBuilderModalRef={
            showGenerateFashionModelPromptBuilderModalRef
          }
        />
      )}
    </div>
  );
};

export default PromptContainer;
